import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AppstoreOutlined,
  BarChartOutlined,
  CalendarOutlined,
  DatabaseOutlined,
  DesktopOutlined,
  DownOutlined,
  HeartOutlined,
  MenuOutlined,
  MessageOutlined,
  NodeIndexOutlined,
  RightOutlined,
  SettingOutlined,
  ShopOutlined,
  TeamOutlined,
} from '@ant-design/icons';
import {
  getCachedCurrentUserMenuRoutes,
  getCurrentUserMenuRoutes,
  type MenuRoute,
} from '@/api/system';

interface SidebarProps {
  collapsed: boolean;
  activeKey: string;
  onMenuClick: (path: string) => void;
}

interface MenuItem {
  key: string;
  path?: string;
  label: string;
  iconName?: string;
  children?: MenuItem[];
}

const homeMenuItem: MenuItem = {
  key: 'fixed-home',
  path: '/',
  label: '首页',
  iconName: 'desktop',
};

// 数据库保存的是原后台组件标识；React 页面 URL 在这里做唯一的适配。
const componentRouteMap: Record<string, string> = {
  'member/applications/index': '/member/review',
  'member/list/index': '/member/list',
  'member/levels/index': '/member/level',
  'member/bills/index': '/member/fees',
  'business/opportunities/index': '/business/list',
  'business/contact-applications/index': '/business/contact',
  'business/top-orders/index': '/business/top-orders',
  'activity/list/index': '/activity/list',
  'activity/signups/index': '/activity/signin',
  'merchant/list/index': '/merchant/list',
  'merchant/writeoff-records/index': '/merchant/writeoff',
  'merchant/reconciliation/index': '/merchant/stats',
  'member/directory/index': '/enterprise/list',
  'member/donations/index': '/donation/overview',
  'community/index': '/community/review',
  'party/index': '/party/list',
  'dashboard/operation/index': '/dashboard/operation',
  'system/user/index': '/settings/accounts',
  'system/role/index': '/settings/roles',
  'system/menu/index': '/settings/menus',
  'system/dict/index': '/settings/dicts',
  'monitor/operlog/index': '/settings/logs',
};

function joinPath(parentPath: string, path?: string) {
  if (!path) return parentPath;
  if (path.startsWith('/')) return path;
  return `${parentPath}/${path}`.replace(/\/+/g, '/');
}

function toMenuItems(routes: MenuRoute[], parentPath = ''): MenuItem[] {
  return routes.flatMap((route, index) => {
    if (route.meta?.hidden) return [];
    const databasePath = joinPath(parentPath, route.path);
    const path = (route.component && componentRouteMap[route.component]) || databasePath;
    const children = toMenuItems(route.children || [], databasePath);
    if (!route.meta?.title && children.length === 0) return [];
    return [{
      key: route.name || path || `${parentPath}-${index}`,
      path: children.length ? undefined : path,
      label: route.meta?.title || route.name || '未命名菜单',
      iconName: route.meta?.icon,
      children: children.length ? children : undefined,
    }];
  });
}

function MenuIcon({ name }: { name?: string }) {
  const normalized = (name || '').toLowerCase();
  if (normalized.includes('desktop') || normalized.includes('home')) return <DesktopOutlined />;
  if (normalized.includes('role') || normalized.includes('user')) return <TeamOutlined />;
  if (normalized.includes('shop') || normalized.includes('document')) return <ShopOutlined />;
  if (normalized.includes('calendar')) return <CalendarOutlined />;
  if (normalized.includes('monitor') || normalized.includes('chart')) return <BarChartOutlined />;
  if (normalized.includes('heart')) return <HeartOutlined />;
  if (normalized.includes('message')) return <MessageOutlined />;
  if (normalized.includes('node-index') || normalized.includes('nodeindex')) return <NodeIndexOutlined />;
  if (normalized.includes('setting') || normalized.includes('system')) return <SettingOutlined />;
  if (normalized.includes('database') || normalized.includes('dict')) return <DatabaseOutlined />;
  if (normalized.includes('menu')) return <MenuOutlined />;
  return <AppstoreOutlined />;
}

export default function Sidebar({ collapsed, activeKey, onMenuClick }: SidebarProps) {
  const [routes, setRoutes] = useState<MenuRoute[]>(() => getCachedCurrentUserMenuRoutes() || []);
  const [expandedKeys, setExpandedKeys] = useState<string[]>([]);
  const [loadError, setLoadError] = useState('');
  const menuItems = useMemo(() => {
    const databaseItems = toMenuItems(routes).filter((item) => item.path !== '/');
    return [homeMenuItem, ...databaseItems];
  }, [routes]);

  useEffect(() => {
    let active = true;
    const loadRoutes = () => {
      setLoadError('');
      getCurrentUserMenuRoutes()
        .then((data) => active && setRoutes(data))
        .catch((error: Error) => active && setLoadError(error.message || '菜单加载失败'));
    };
    if (!getCachedCurrentUserMenuRoutes()) loadRoutes();
    window.addEventListener('menu-routes-changed', loadRoutes);
    return () => {
      active = false;
      window.removeEventListener('menu-routes-changed', loadRoutes);
    };
  }, []);

  useEffect(() => {
    const findParents = (items: MenuItem[], parents: string[] = []): string[] | undefined => {
      for (const item of items) {
        if (item.path === activeKey) return parents;
        const found = findParents(item.children || [], [...parents, item.key]);
        if (found) return found;
      }
    };
    const parents = findParents(menuItems);
    if (parents?.length) setExpandedKeys((prev) => [...new Set([...prev, ...parents])]);
  }, [activeKey, menuItems]);

  const toggleExpand = useCallback((key: string) => {
    setExpandedKeys((prev) => prev.includes(key) ? prev.filter((item) => item !== key) : [...prev, key]);
  }, []);

  const renderMenuItem = (item: MenuItem, depth = 0) => {
    const hasChildren = Boolean(item.children?.length);
    const expanded = expandedKeys.includes(item.key);
    const active = item.path === activeKey;
    return (
      <div key={item.key}>
        <div
          className={`flex items-center cursor-pointer transition-all duration-200 ${active ? 'text-white bg-[#1890ff]' : 'text-white/70 hover:text-white'} ${collapsed && depth === 0 ? 'justify-center px-0 py-3' : 'px-4 py-3'} ${depth > 0 ? 'pl-12' : ''}`}
          onClick={() => hasChildren ? toggleExpand(item.key) : item.path && onMenuClick(item.path)}
          title={collapsed ? item.label : undefined}
        >
          <span className={`flex items-center justify-center ${collapsed && depth === 0 ? 'text-lg' : 'text-base mr-3'}`}><MenuIcon name={item.iconName} /></span>
          {!collapsed && <><span className="flex-1 text-sm">{item.label}</span>{hasChildren && (expanded ? <DownOutlined /> : <RightOutlined />)}</>}
        </div>
        {hasChildren && expanded && !collapsed && item.children!.map((child) => renderMenuItem(child, depth + 1))}
      </div>
    );
  };

  return (
    <aside className={`fixed left-0 top-0 h-full bg-[#122c52] z-50 transition-all duration-300 ${collapsed ? 'w-16' : 'w-62'}`}>
      <div className="h-14 flex items-center px-4 border-b border-white/10">
        <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 overflow-hidden">
          <img src="https://static.readdy.ai/image/f4e14b58753752861061999bf16a9230/bf42b38cc5f41d849780497acba9aba6.png" alt="logo" className="w-full h-full object-cover" />
        </div>
        {!collapsed && <span className="ml-3 text-white font-medium text-sm">儋州市那大镇商会数字化平台</span>}
      </div>
      <div className="py-2 overflow-y-auto" style={{ height: 'calc(100% - 56px)' }}>
        {loadError && !collapsed && <div className="px-4 py-3 text-xs text-red-300">{loadError}</div>}
        {!loadError && menuItems.length === 1 && !collapsed && <div className="px-4 py-3 text-xs text-white/50">暂无其他可用菜单</div>}
        {menuItems.map((item) => renderMenuItem(item))}
      </div>
    </aside>
  );
}
